import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi, BACKEND } from "@/lib/api";
import { PageHeader, StatusBadge, Btn, Panel } from "@/lib/ui";
import { HistorySidebar } from "@/components/HistorySidebar";
import { Send, Cpu, Headset, Copy, TrendingUp, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

const LS_KEY = "ggg:concierge:active";

export default function Concierge() {
  const api = useApi();
  const qc = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [showArchived, setShowArchived] = useState(false);
  const endRef = useRef(null);
  const bootstrapped = useRef(false);

  const { data: insights } = useQuery({ queryKey: ["concierge-insights"], queryFn: () => api.get("/concierge/insights") });
  const convs = useQuery({ queryKey: ["concierge-convs", showArchived], queryFn: () => api.get(`/concierge/conversations${showArchived ? "?include_archived=true" : ""}`) });

  const embed = `<script src="${BACKEND}/api/public/concierge/widget.js" async></script>`;
  const copyEmbed = () => { navigator.clipboard.writeText(embed); toast.success("Embed snippet copied"); };

  useEffect(() => {
    if (bootstrapped.current || !convs.data) return;
    bootstrapped.current = true;
    if (!convId && convs.data.length > 0) setConvId(convs.data[0].id);
  }, [convs.data, convId]);

  useEffect(() => {
    if (!convId) { setMessages([]); localStorage.removeItem(LS_KEY); return; }
    localStorage.setItem(LS_KEY, convId);
    let cancelled = false;
    api.get(`/concierge/conversations/${convId}`)
      .then((c) => { if (!cancelled) setMessages((c.messages || []).map((mm, i) => ({ id: `${convId}-${i}`, ...mm }))); })
      .catch(() => { if (!cancelled) setConvId(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = useMutation({
    mutationFn: (message) => api.post("/concierge/message", { message, conversation_id: convId }),
    onSuccess: (r) => {
      if (!convId) { setConvId(r.conversation_id); localStorage.setItem(LS_KEY, r.conversation_id); }
      setMessages((m) => [...m, { id: `${Date.now()}-a`, role: "assistant", content: r.reply, tokens: r.tokens, model: r.model }]);
      qc.invalidateQueries({ queryKey: ["concierge-convs"] });
    },
    onError: (e) => { toast.error(e.message); setMessages((m) => [...m, { id: `${Date.now()}-e`, role: "assistant", content: `⚠ ${e.message}`, error: true }]); },
  });

  const submit = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || send.isPending) return;
    setMessages((m) => [...m, { id: `${Date.now()}-u`, role: "user", content: msg }]);
    setInput("");
    send.mutate(msg);
  };

  const newChat = () => { setConvId(null); setMessages([]); localStorage.removeItem(LS_KEY); };
  const rename = (id, title) => api.patch(`/concierge/conversations/${id}`, { title }).then(() => qc.invalidateQueries({ queryKey: ["concierge-convs"] }));
  const archive = (id, archived = true) => api.patch(`/concierge/conversations/${id}`, { archived }).then(() => { toast.success(archived ? "Archived" : "Restored"); if (archived && id === convId) newChat(); qc.invalidateQueries({ queryKey: ["concierge-convs"] }); });
  const del = (id) => api.del(`/concierge/conversations/${id}`).then(() => { toast.success("Deleted"); if (id === convId) newChat(); qc.invalidateQueries({ queryKey: ["concierge-convs"] }); });

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader kicker="GGG-13 · Public Key" title="Public Concierge">
        <StatusBadge tone="gold" dot="pulse">Live · Embeddable</StatusBadge>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Panel className="p-4" data-testid="concierge-embed">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Embed on your website</span>
            <Btn variant="ghost" size="sm" onClick={copyEmbed} data-testid="concierge-copy-embed"><Copy className="h-3.5 w-3.5" /> Copy</Btn>
          </div>
          <code className="block text-xs font-mono text-primary bg-surface border border-border rounded-md p-3 break-all">{embed}</code>
          <p className="text-xs text-muted-foreground mt-2">Paste before <span className="font-mono">&lt;/body&gt;</span> on gopigadhvi.com. A gold "Chat with us" bubble appears bottom-right.</p>
        </Panel>
        <Panel className="p-4" data-testid="concierge-insights">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Concierge insights</span>
            <Link to="/leads" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1" data-testid="concierge-open-inbox">
              {insights?.leads ?? 0} leads <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div><div className="font-head text-xl font-bold" data-testid="insight-conversations">{insights?.conversations ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Chats</div></div>
            <div><div className="font-head text-xl font-bold" data-testid="insight-messages">{insights?.visitor_messages ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Questions</div></div>
            <div><div className="font-head text-xl font-bold text-ok" data-testid="insight-conversion">{insights?.conversion_rate ?? 0}%</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead rate</div></div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Most asked</div>
          <div className="space-y-1 max-h-24 overflow-y-auto" data-testid="top-questions">
            {(!insights?.top_questions || insights.top_questions.length === 0) && <p className="text-xs text-muted-foreground">No questions yet.</p>}
            {(insights?.top_questions || []).slice(0, 6).map((q, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-foreground/85 truncate">{q.question}</span>
                <span className="font-mono text-primary shrink-0">{q.count}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4 min-h-0">
        <div className="hidden lg:block min-h-0">
          <HistorySidebar
            items={convs.data || []} activeId={convId} loading={convs.isLoading}
            onSelect={setConvId} onNew={newChat} onRename={rename} onArchive={archive} onDelete={del}
            newLabel="New Chat" heading="Preview chats" testidPrefix="concierge"
            showArchived={showArchived} onToggleArchived={setShowArchived}
          />
        </div>

        <div className="flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto border border-border rounded-md bg-surface/50 p-4 space-y-4">
            {messages.length === 0 && !send.isPending && (
              <div className="max-w-xl">
                <div className="flex items-center gap-2 text-primary mb-3"><Headset className="h-5 w-5" /><span className="font-head text-lg font-bold">GGG Public Concierge</span></div>
                <p className="text-sm text-muted-foreground">Preview how the public assistant will greet and help visitors of the GGG website. Your preview chats are saved and appear in the sidebar.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["Tell me about the Gopi Gadhvi Group.", "What services do you offer?", "How can I get in touch?"].map((s) => (
                    <button key={s} onClick={() => submit(s)} className="text-xs border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[80%] rounded-md px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed",
                  m.role === "user" ? "bg-primary/15 border border-primary/30" : cn("bg-surface-elevated border-l-2 border-primary pl-4", m.error && "border-err bg-err/5"))}>
                  {m.role === "assistant" && <div className="text-[10px] font-mono uppercase tracking-wider text-primary mb-1.5">Concierge {m.model ? `· ${m.model}` : ""}</div>}
                  {m.content}
                </div>
              </div>
            ))}
            {send.isPending && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Cpu className="h-4 w-4 animate-spin text-primary" /> Concierge is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Ask the public concierge…"
              className="flex-1 h-11 rounded-md border border-border bg-surface px-4 text-sm focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none" />
            <Btn onClick={() => submit()} disabled={send.isPending} className="h-11 px-5" data-testid="concierge-send"><Send className="h-4 w-4" /> Send</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
